import React from 'react';
import { DefaultButton, Label, PrimaryButton, Separator, Spinner, SearchBox, Dropdown, IDropdownOption } from "@fluentui/react";
import { Add24Regular } from '@fluentui/react-icons';
import { connect } from "react-redux";
import { useBoolean } from '@fluentui/react-hooks';
import { ErrorMessageComponent } from "../../../Error/ErrorMessageComponent";
import { useEffect, useMemo, useRef, useState } from "react";
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import ConceptFormComponent from "../ConceptForm/ConceptForm.Component";
import { ConceptItemComponent } from "./ConceptItem.Component";
import { Toast, ToastBody, ToastTitle, useToastController } from '@fluentui/react-components';

// functions
import * as ComponentFunctions from './ConceptList.Functions';

////i18Next
import { useTranslation } from 'react-i18next';

// styles
import './ConceptList.Styles.css';

export const ConceptListComponent = (props: ComponentFunctions.Props) => {

    // initial values
    const [isOpenInitialSettings, { setTrue: openPanelInitialSettings, setFalse: dismissPanelInitialSettings }] = useBoolean(false);
    const [selectedConcept, setSelectedConcept] = useState<ContractConcept | undefined>(undefined);
    const [filterText, setFilterText] = useState<string>("");
    const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
    const [cloneInProgress, setCloneInProgress] = useState<boolean>(false);
    const [cloningConceptId, setCloningConceptId] = useState<number | null>(null);
    const cloneLoadingStartedRef = useRef(false);

    // Add or eddit Concept
    const [isOpenVisit, { setTrue: openPanelConcept, setFalse: dismissPanelConcept }] = useBoolean(false);

    const handleInitialSettingsClosePanel = () => {
        dismissPanelInitialSettings();
    };

    const handleConceptClosePanel = () => {
        setSelectedConcept(undefined);
        dismissPanelConcept();
    };

    const handleChargeVisitInformation = () => {
        openPanelInitialSettings();
    };

    const HandleAddEvent = () => {
        openPanelConcept();
    };

    const HandleEditEvent = (concept: ContractConcept) => {
        setSelectedConcept(concept);
        openPanelConcept();
    };

    const HandleDeleteEvent = (concept: ContractConcept) => {
        props.DeleteContractConcept(concept.id, props.ContractId, props.ContractAddendumId);
    };

    const HandleCloneEvent = (concept: ContractConcept) => {
        cloneLoadingStartedRef.current = false;
        setCloneInProgress(true);
        setCloningConceptId(concept.id);
        props.CloneContractConcept(props.ContractId, props.ContractAddendumId, concept.id);
    };

    //useTransaltion()
    const [t] = useTranslation();
    const { dispatchToast } = useToastController('PrebillerToast');

    const resolveBranchId = (branch: any): number | undefined => {
        const rawId = branch?.branchId ?? branch?.id;
        const parsedId = Number(rawId);
        return Number.isFinite(parsedId) ? parsedId : undefined;
    };

    const getConceptBranchIds = (concept: ContractConcept): number[] => {
        const ids = new Set<number>();

        (concept.branches || []).forEach((branch) => {
            const id = resolveBranchId(branch);
            if (id !== undefined) {
                ids.add(id);
            }
        });

        const directCandidates = [
            (concept as any)?.branchId,
            (concept as any)?.branchid,
            (concept as any)?.concept?.branchId,
            (concept as any)?.concept?.branchid,
            (concept as any)?.concept?.branch?.id,
            (concept as any)?.concept?.branch?.branchId,
        ];

        directCandidates.forEach((raw) => {
            const parsed = Number(raw);
            if (Number.isFinite(parsed)) {
                ids.add(parsed);
            }
        });

        return Array.from(ids);
    };

    // efects
    useEffect(() => {
        if (props.Contract) {
            props.GetAllContractsConceptByAddendum(props.ContractId, props.ContractAddendumId);
        }
    }, [props.Contract === undefined, props.ContractAddendumId]);

    useEffect(() => {
        if (!cloneInProgress) {
            return;
        }

        if (props.isLoading) {
            cloneLoadingStartedRef.current = true;
            return;
        }

        if (!cloneLoadingStartedRef.current) {
            return;
        }

        cloneLoadingStartedRef.current = false;

        if (props.failOnLoading) {
            dispatchToast(
                <Toast>
                    <ToastTitle>{t('conceptList.toast.cloneError', 'No se pudo clonar el concepto')}</ToastTitle>
                    {props.error?.ErrorMessage && (
                        <ToastBody>{props.error.ErrorMessage}</ToastBody>
                    )}
                </Toast>,
                { intent: 'error', timeout: 4500 }
            );
        } else {
            dispatchToast(
                <Toast>
                    <ToastTitle>{t('conceptList.toast.cloneSuccess', 'Concepto clonado correctamente')}</ToastTitle>
                </Toast>,
                { intent: 'success', timeout: 3000 }
            );
        }

        setCloneInProgress(false);
        setCloningConceptId(null);
    }, [cloneInProgress, props.isLoading, props.failOnLoading, dispatchToast, t]);

    const branchOptions: IDropdownOption[] = useMemo(() => {
        const namesById = new Map<number, string>();

        (props.Addendum?.branches || []).forEach((branch) => {
            namesById.set(branch.id, (branch.branchName || '').trim() || `#${branch.id}`);
        });

        (props.ContractConcepts || []).forEach((concept) => {
            (concept.branches || []).forEach((branch) => {
                const id = resolveBranchId(branch);
                if (id === undefined) {
                    return;
                }
                const name = (branch.branchName || '').trim() || namesById.get(id) || `#${id}`;
                if (!namesById.has(id)) {
                    namesById.set(id, name);
                }
            });

            getConceptBranchIds(concept).forEach((id) => {
                if (!namesById.has(id)) {
                    namesById.set(id, `#${id}`);
                }
            });
        });

        return Array.from(namesById.entries())
            .map(([id, name]) => ({ key: id, text: name }))
            .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));
    }, [props.Addendum?.branches, props.ContractConcepts]);

    const handleBranchFilterChange = (
        _event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption
    ) => {
        if (!option) {
            return;
        }

        const optionId = Number(option.key);
        setSelectedBranchIds((current) =>
            option.selected
                ? [...current, optionId]
                : current.filter((id) => id !== optionId)
        );
    };

    // Loading
    const Loading = props.isLoading && (
        <div>
            <Spinner
                label={t('conceptList.loading.loadingConcepts')}
                ariaLive="assertive"
                labelPosition="top"
            />
        </div>
    );

    const ComponentActions = (
        <div className="ConceptListActions">
            <PrimaryButton
                secondaryText={t('conceptList.componentActions.addConcept')}
                onClick={HandleAddEvent}
                text={t('conceptList.componentActions.addConcept')}
                onRenderIcon={() => <Add24Regular />}
                className="actionButton"
            />
            <SearchBox
                className="conceptSearch"
                placeholder={t('conceptList.search.placeholder', 'Buscar conceptos por nombre, referencia o descripción')}
                value={filterText}
                onChange={(_, newValue) => setFilterText(newValue || "")}
                onClear={() => setFilterText("")}
                styles={{ root: { maxWidth: 380, margin: '8px 0' } }}
            />
            <Dropdown
                className="conceptBranchFilter"
                placeholder={t('conceptList.filters.branchesPlaceholder', 'Filtrar por rama')}
                selectedKeys={selectedBranchIds}
                options={branchOptions}
                onChange={handleBranchFilterChange}
                multiSelect
                disabled={branchOptions.length === 0}
                styles={{ dropdown: { minWidth: 250, margin: '8px 0' } }}
            />
        </div>
    );

    const ErrorMessage = props.failOnLoading && props.error && (
        <ErrorMessageComponent Error={props.error} />
    );

    const NoRegistries = (props.isLoadingSuccessfully && props.ContractConcepts?.length === 0 || props.ContractConcepts?.length === undefined) && (
        <div className="NoDataSection">
            <img
                src={require('../../../../assets/img/empty_state.gif')}
                alt="Ichtys"
                style={{ width: '10em', height: '10em', marginBottom: '1em' }}
            ></img>
            <Label>{t('conceptList.noRegistries.noConceptsRegistered.noConceptsRegistered')}</Label>
        </div>
    );

    const Forms = <>
        <ConceptFormComponent
            isOpen={isOpenVisit}
            openPanel={openPanelConcept}
            dismissPanel={handleConceptClosePanel}
            ContractId={props.ContractId}
            ContractAddendumId={props.ContractAddendumId}
            ContractConceptId={selectedConcept?.id}
            CurrentConcepts={props.Contract?.concepts || []}
            CurrentStages={props.Addendum?.stages || []}
        />
    </>;

    const normalized = (v?: string) => (v || "").toString().toLowerCase();
    const term = normalized(filterText);
    const filteredConcepts = term
        ? props.ContractConcepts.filter(cc => {
            const n = normalized(cc.concept?.conceptName);
            const d = normalized(cc.concept?.conceptDescription);
            const r = normalized(cc.concept?.conceptReferenceId as any);
            return n.includes(term) || d.includes(term) || r.includes(term);
        })
        : props.ContractConcepts;

    const branchFilteredConcepts = selectedBranchIds.length === 0
        ? filteredConcepts
        : filteredConcepts.filter((cc) => {
            const conceptBranchIds = getConceptBranchIds(cc);
            if (conceptBranchIds.length === 0) {
                return false;
            }
            const branchIds = new Set(conceptBranchIds);
            return selectedBranchIds.some((id) => branchIds.has(id));
        });

    const conceptNameById = useMemo(() => {
        const lookup: Record<number, string> = {};
        (props.ContractConcepts || []).forEach((concept) => {
            if (typeof concept.id === 'number' && concept.id > 0) {
                const name = (concept.concept?.conceptName || '').trim();
                if (name.length > 0) {
                    lookup[concept.id] = name;
                }
            }
        });
        return lookup;
    }, [props.ContractConcepts]);

    return (
        <>
            {ComponentActions}
            <Separator />
            {ErrorMessage}
            {props.isLoading ? Loading : null}
            <div className="coi-list">
                {branchFilteredConcepts?.map((concept: ContractConcept, index) => (
                    <ConceptItemComponent
                        Concept={concept} index={index}
                        ConceptNameById={conceptNameById}
                        isCloning={cloningConceptId === concept.id}
                        onEdit={(currentConcept: ContractConcept) => HandleEditEvent(currentConcept)}
                        onDelete={(currentConcept: ContractConcept) => HandleDeleteEvent(currentConcept)}
                        onClone={(currentConcept: ContractConcept) => HandleCloneEvent(currentConcept)}
                        onToggleEnabled={(currentConcept: ContractConcept, enabled: boolean) =>
                            props.SetContractConceptEnabled(currentConcept.id, enabled, props.ContractId, props.ContractAddendumId)}
                        onToggleApproved={(currentConcept: ContractConcept, approved: boolean) =>
                            props.SetContractConceptApproved(currentConcept.id, approved, props.ContractId, props.ContractAddendumId)}
                        isDeleting={props.isDeleting}
                        failonDeleting={props.failOnDeleting}
                        error={props.error}
                    />
                ))}
            </div>
            {NoRegistries}
            {Forms}
        </>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConceptListComponent as any);